package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.capacity.monthRange
import com.evoapps.evoshift.domain.capacity.within
import com.evoapps.evoshift.domain.legal.DayClosure
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import com.evoapps.evoshift.domain.model.DayTemplate
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.shift.ShiftFactory
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.number
import kotlinx.datetime.plus

/** What copying the weekly skeleton into a month did, so the app can say so. */
sealed interface SkeletonOutcome {
    /** [closedDays]: days of the month the Sunday policy closes (SH-55), left without shifts. */
    data class Copied(val shifts: Int, val closedDays: Int = 0) : SkeletonOutcome
    /** No day template is assigned to any weekday. */
    data object NoWeek : SkeletonOutcome
    /** Days have templates, but those templates hold no (existing) shifts. */
    data object EmptyTemplates : SkeletonOutcome
}

/** A workplace's day and shift templates, read together (see [ApplyWeeklySkeletonUseCase.loadTemplates]). */
data class SkeletonTemplates(val dayTemplates: List<DayTemplate>, val shiftTemplates: List<ShiftTemplate>)

/**
 * Materialises the workplace's weekly skeleton (day of week -> day template -> shift templates)
 * into OPEN shifts (`userId = null`, the demand) for every day of the month, except the days the
 * workplace's Sunday policy closes (SH-55: non-trading Sundays, public holidays).
 */
class ApplyWeeklySkeletonUseCase(
    private val workplaceRepository: WorkplaceRepository,
    private val shiftRepository: ShiftRepository,
    private val getDayClosures: GetDayClosuresUseCase = GetDayClosuresUseCase()
) {
    companion object {
        const val CREATED_BY = "SYSTEM_SKELETON"
    }

    suspend operator fun invoke(
        workplaceId: String,
        rosterId: String,
        year: Int,
        month: Int
    ): Result<SkeletonOutcome> = runCatching {
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
        if (workplace.settings.defaultWeeklySkeleton.isEmpty()) return@runCatching SkeletonOutcome.NoWeek
        val templates = loadTemplates(workplaceId).getOrThrow()
        val (outcome, shifts) = plan(workplace, templates, rosterId, year, month, closures(workplace, year, month))
        if (shifts.isNotEmpty()) write(shifts).getOrThrow()
        outcome
    }

    /**
     * SH-48: the weekly skeleton for the days of [range] (cut to the month) that have no shifts
     * yet ([existingShifts] are the roster's): the days already shaped are left as they are.
     * [SkeletonOutcome.Copied] with 0 when every day of the range already has shifts (or its
     * weekdays have no template).
     */
    suspend fun fillDays(
        workplaceId: String,
        rosterId: String,
        year: Int,
        month: Int,
        range: ScheduleRange,
        existingShifts: List<Shift>
    ): Result<SkeletonOutcome> = runCatching {
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
        if (workplace.settings.defaultWeeklySkeleton.isEmpty()) return@runCatching SkeletonOutcome.NoWeek
        val days = range.within(monthRange(year, month)) ?: return@runCatching SkeletonOutcome.Copied(0)
        val shaped = existingShifts.filter { it.rosterId == rosterId }.map { it.date }.toSet()
        val templates = loadTemplates(workplaceId).getOrThrow()
        // SH-55: the days the Sunday policy closes stay without shifts here too.
        val (outcome, shifts) = plan(workplace, templates, rosterId, year, month, closures(workplace, year, month)) { date ->
            date in days.start..days.endInclusive && date.toString() !in shaped
        }
        if (shifts.isNotEmpty()) write(shifts).getOrThrow()
        if (outcome == SkeletonOutcome.EmptyTemplates) SkeletonOutcome.Copied(0) else outcome
    }

    /**
     * SH-194: the month was created while the week had no day template for some weekdays, and the
     * manager has just been to the Weekly Skeleton. When the week differs from [weekBefore], the
     * skeleton is copied into the days of the month that still have no shifts (the days already
     * filled are left as they are); when it does not, nothing is written and null is returned.
     */
    suspend fun fillEmptyDaysIfWeekChanged(
        workplaceId: String,
        rosterId: String,
        year: Int,
        month: Int,
        weekBefore: Map<Int, String>
    ): Result<SkeletonOutcome?> = runCatching {
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
        val week = workplace.settings.defaultWeeklySkeleton
        if (week == weekBefore) return@runCatching null
        if (week.isEmpty()) return@runCatching SkeletonOutcome.NoWeek
        val shaped = shiftRepository.getShiftsForRoster(rosterId).getOrThrow().map { it.date }.toSet()
        val templates = loadTemplates(workplaceId).getOrThrow()
        val (outcome, shifts) = plan(workplace, templates, rosterId, year, month, closures(workplace, year, month)) { date ->
            date.toString() !in shaped
        }
        if (shifts.isNotEmpty()) write(shifts).getOrThrow()
        outcome
    }

    /** The day and shift templates, both collections read at the same time. */
    suspend fun loadTemplates(workplaceId: String): Result<SkeletonTemplates> = runCatching {
        coroutineScope {
            val days = async { workplaceRepository.getDayTemplates(workplaceId).getOrThrow() }
            val shifts = async { workplaceRepository.getShiftTemplates(workplaceId).getOrThrow() }
            SkeletonTemplates(days.await(), shifts.await())
        }
    }

    /** The days of the month [workplace]'s Sunday policy closes (none for an OPEN workplace, without a read). */
    suspend fun closures(workplace: Workplace, year: Int, month: Int): Map<LocalDate, DayClosure> =
        getDayClosures(workplace.settings, year, month)

    /**
     * The open shifts the skeleton puts into the month, without writing them (CreateRosterUseCase
     * writes them together with the rest of the month). [closures] ([closures]) get no shifts.
     */
    fun plan(
        workplace: Workplace,
        templates: SkeletonTemplates,
        rosterId: String,
        year: Int,
        month: Int,
        closures: Map<LocalDate, DayClosure> = emptyMap(),
        /** Only these days of the month get shifts (SH-48: a day range, days left as they are). */
        includeDay: (LocalDate) -> Boolean = { true }
    ): Pair<SkeletonOutcome, List<Shift>> {
        val skeleton = workplace.settings.defaultWeeklySkeleton
        if (skeleton.isEmpty()) return SkeletonOutcome.NoWeek to emptyList()

        val dayTemplates = templates.dayTemplates.associateBy { it.id }
        val shiftTemplates = templates.shiftTemplates.associateBy { it.id }
        val shifts = generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }
            .takeWhile { it.month.number == month }
            .filter { it !in closures }
            .filter(includeDay)
            .flatMap { date ->
                val dayTemplate = skeleton[date.dayOfWeek.isoDayNumber]?.let { dayTemplates[it] }
                dayTemplate?.shiftTemplateIds.orEmpty().asSequence()
                    .mapNotNull { shiftTemplates[it] } // a deleted template is skipped, not fatal
                    .map { template -> ShiftFactory.fromTemplate(rosterId, date, template, createdBy = CREATED_BY) }
            }
            .toList()

        return if (shifts.isEmpty()) SkeletonOutcome.EmptyTemplates to emptyList() else SkeletonOutcome.Copied(shifts.size, closures.size) to shifts
    }

    /** Writes [plan]ned shifts (batches of 450). */
    suspend fun write(shifts: List<Shift>): Result<Unit> = shiftRepository.createShifts(shifts)
}
